import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import type { Session, User } from "@supabase/supabase-js"
import { getSupabase } from "../config/supabaseClient"

/**
 * Portado de my-calendar-app/src/context/AuthContext.jsx, tipado, con dos cambios:
 *
 *  · SIN `register`. Este panel NO ofrece auto-registro: no se porta el flujo
 *    ?registro=<SIGNUP_SECRET> de calendar-app. Las cuentas las crea IT en el
 *    dashboard de Supabase (o con el invite de calendar-app, que usa el MISMO pool
 *    auth.users porque es el mismo proyecto Supabase).
 *  · `claimIsAdmin` se llama así, y no `isAdmin`, para que el nombre recuerde que es
 *    solo el CLAIM del JWT — sirve para renderizar optimistamente y evitar un flash,
 *    pero la fuente de verdad es GET /api/dashboard/me. Ver AdminGate.
 */

interface AuthValue {
  session: Session | null
  user: User | null
  claimIsAdmin: boolean
  loading: boolean
  recoveryMode: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  setPassword: (newPassword: string) => Promise<void>
  requestPasswordReset: (email: string) => Promise<void>
  refreshSession: () => Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

/**
 * Los mails de invite y de reset vuelven con un hash tipo
 * #access_token=…&type=recovery. Se detecta en el primer load para mostrar la pantalla
 * de elegir contraseña en vez del tablero.
 */
function hashHasRecoveryType(): boolean {
  const hash = window.location.hash || ""
  return hash.includes("type=invite") || hash.includes("type=recovery")
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [recoveryMode, setRecoveryMode] = useState(hashHasRecoveryType())

  useEffect(() => {
    const supabase = getSupabase()

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setLoading(false)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      // ⚠ NUNCA hacer `await` de otra llamada de supabase-js DENTRO de este callback:
      // supabase-js v2 sostiene un lock interno y puede deadlockear. Solo setState
      // acá; cualquier otra cosa se difiere con setTimeout(fn, 0).
      //
      // calendar-app cumple de casualidad; acá se dice explícito porque AdminGate
      // dispara un fetch a /api/dashboard/me cuando aparece la sesión — y lo hace
      // desde un useEffect propio, no desde acá, justamente por esto.
      setSession(newSession)
      setLoading(false)
      if (event === "PASSWORD_RECOVERY") setRecoveryMode(true)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await getSupabase().auth.signInWithPassword({ email, password })
    if (error) throw error
  }, [])

  const signOut = useCallback(async () => {
    const { error } = await getSupabase().auth.signOut()
    if (error) throw error
  }, [])

  /** Completa un invite o un reset de contraseña. */
  const setPassword = useCallback(async (newPassword: string) => {
    const { error } = await getSupabase().auth.updateUser({ password: newPassword })
    if (error) throw error
    setRecoveryMode(false)
    // El hash trae el access_token: se limpia para que un refresh no vuelva a entrar
    // en modo recovery.
    window.history.replaceState(null, "", window.location.pathname + window.location.search)
  }, [])

  const requestPasswordReset = useCallback(async (email: string) => {
    // OJO: el allowlist de redirects de Supabase es DE TODO EL PROYECTO, y el proyecto
    // es compartido con calendar-app. Este origen + /dashboard tiene que estar en
    // Auth → URL Configuration → Redirect URLs, o el link rebota al Site URL (o sea,
    // al calendario). El template del mail tampoco se puede customizar por app: por eso
    // la pantalla SetPassword se auto-identifica.
    const { error } = await getSupabase().auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/dashboard`,
    })
    if (error) throw error
  }, [])

  const refreshSession = useCallback(async () => {
    const { data, error } = await getSupabase().auth.refreshSession()
    if (error) throw error
    setSession(data.session)
  }, [])

  const user = session?.user ?? null

  const value = useMemo<AuthValue>(
    () => ({
      session,
      user,
      claimIsAdmin: (user?.app_metadata as { role?: string } | undefined)?.role === "admin",
      loading,
      recoveryMode,
      signIn,
      signOut,
      setPassword,
      requestPasswordReset,
      refreshSession,
    }),
    [
      session,
      user,
      loading,
      recoveryMode,
      signIn,
      signOut,
      setPassword,
      requestPasswordReset,
      refreshSession,
    ]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth() fuera de <AuthProvider>")
  return ctx
}
